/**
 * User Profile Store - manages logged-in user profile data, addresses, payment methods, and preferences.
 *
 * **Key responsibilities:**
 * - Fetch and cache user profile from backend API
 * - Manage shipping/billing addresses (CRUD operations)
 * - Save credit cards and gift cards (🔒 PCI-sensitive)
 * - Update basic info (name, phone, birthdate, zip)
 * - Manage interests and preferences
 * - Track loyalty program membership and points
 * - Handle special profile types (athlete, employee, IPA, Wranx)
 *
 * **🔒 Security & PII warnings:**
 * - **NEVER log full credit card numbers or CVV**
 * - Phone numbers stored in E.164 format (validated/formatted)
 * - Email addresses are PII (handle with care)
 * - Addresses contain PII (name, phone, street)
 * - Payment instrument IDs are sensitive (backend-generated tokens)
 *
 * **Multi-brand notes:**
 * - All brands use same user profile structure
 * - Brand-specific loyalty programs (LaunchDarkly flags)
 * - Employee/athlete discounts vary by brand
 * - IPA/Wranx profiles specific to certain brands
 *
 * **Profile types:**
 * - **Consumer**: Standard user profile
 * - **Employee**: VF employee with annual discount cap
 * - **Athlete**: Sponsored athlete with annual credit
 * - **IPA**: Industry Pro Account (pros/influencers)
 * - **Wranx**: Western industry professionals
 *
 * **State management:**
 * - Profile loaded lazily (not immediate on store creation)
 * - `pending` tracks loading state
 * - `error` tracks API errors
 * - `execute()` method triggers profile load
 *
 * **Address handling:**
 * - Addresses have approachType: 'S' (shipping) or 'B' (billing)
 * - Each address has unique ID (e.g., 'S123456', 'B789012')
 * - `main` flag indicates default address per type
 * - Phone numbers formatted to E.164 before API calls
 *
 * **Payment methods:**
 * - Credit cards: tokenized, never store raw card data
 * - Gift cards: stored as payment instruments
 * - Requires reCAPTCHA for credit card save
 *
 * **Enrollment states:**
 * - LOOKUP: User not logged in
 * - COMPLETE_PROFILE_MOBILE: Missing phone number
 * - COMPLETE_PROFILE_SUBSCRIPTION: Post-auth onboarding
 * - COMPLETE_PROFILE_PROFILE: Missing core profile data
 *
 * **Critical composable rules:**
 * - ⚠️ **Always call in component setup** (not inside methods/callbacks)
 * - ⚠️ **Cache store reference**, don't call useUserStore() repeatedly
 * - ⚠️ **Never call inside onResponse hooks** (causes reactivity loss)
 *
 * @see domains/commerce/stores/auth.ts for authentication
 * @see domains/commerce/components/account for UI integration
 * @see CONTRIBUTING.md#vuenuxt-composables---critical-rules for setup-only composable rules
 */

import type { AddAddressPayload, UpdateAddressPayload } from '#root/api/clients/consumer/data-contracts'
import type { BaseAddress } from '#types/components/account/address'
import { ApiErrorCode } from '#root/enums/api'
import { EnrollmentState } from '#root/enums/profile'

// TODO better type support for Profile Payloads GLOBAL15-106267. ProfileInfoData['consumerProfile']['consumerDetails'] can't be used because it's optional
type ConsumerDetails = {
  firstName?: string
  lastName?: string
  phone?: string
  phoneCode?: string
  postalCode?: string
  gender?: 'M' | 'F' | 'NA' | string
  birthDate?: string
  source?: {
    campaignName?: string
  }
}

export const useUserStore = defineStore('user', () => {
  const auth = useAuthStore()
  const { hideEmployeePoints } = useAppConfig().components.vf.loyalty
  const featureFlags = useFeatureFlags()
  const { consumer } = useApi()
  const queryParams = ref({
    includeLoyaltySummary:
      featureFlags.enableLoyaltyFeatures && !featureFlags.showLoyaltyRewardsMilestoneBar
        ? true
        : undefined
  })

  const { showInterestsStep } = useAppConfig().components.dialog.simplifiedEnrollment

  // ────────────────────────────────────────────────────────────────────────────
  // API Hook (Lazy Profile Loading)
  // ────────────────────────────────────────────────────────────────────────────

  const {
    data,
    pending,
    error,
    execute
  } = consumer.getProfileInfo(queryParams.value, {
    immediate: false,
    onResponse({ response: { _data } }) {
      if (_data?.consumerProfile?.consumerDetails?.lwaUserInfo)
        auth.lwaProfile = _data.consumerProfile.consumerDetails.lwaUserInfo
    }
  })
  // setting to false because with { immediate: false } pending is still initially true: https://github.com/nuxt/nuxt/issues/19333
  pending.value = false

  // ────────────────────────────────────────────────────────────────────────────
  // Computed State (Derived from Profile)
  // ────────────────────────────────────────────────────────────────────────────

  const { $formatPrice, $t, $feature } = useNuxtApp()

  /** Full consumer profile object */
  const profile = computed(() => data.value?.consumerProfile)

  /** Personalized offer cards sourced from active offers available to the member - [Zeta] */
  const activeOffers = computed(() => data.value?.consumerProfile?.activeOffers || [])

  /** Consumer details (personal info, addresses, identity) */
  const details = computed(() => profile.value?.consumerDetails)

  /** Current loyalty points */
  const loyaltyPoints = computed(() => profile.value?.loyaltySummary?.points)

  /** Is user a VF employee? (annual discount cap) */
  const isEmployeeProfile = computed(() => !!details.value?.employee)

  /** Is user enrolled in loyalty program? */
  const isLoyaltyMember = computed(() => profile.value?.isLoyaltyMember)

  /** Can this loyalty member access feature-flagged loyalty experiences? */
  const enableLoyaltyFeatures = computed(() =>
    featureFlags.enableLoyaltyFeatures && !!isLoyaltyMember.value
  )

  /** Loyalty opt-in date (when user joined loyalty program) */
  const loyaltyOptInDate = computed(() => profile.value?.loyaltyOptInDate)

  /** Current loyalty points balance */
  const loyaltyPointsBalance = computed(() => {
    if (isEmployeeProfile.value && hideEmployeePoints) {
      return 0
    }

    return profile.value?.loyaltySummary?.points?.balance || 0
  })

  /** All addresses (shipping + billing) */
  const addresses = computed(() => details.value?.address || [])

  /** Shipping addresses only (sorted by default) */
  const shippingAddresses = computed(() => sortAddressByDefault(addresses.value.filter((address) => address.approachType === 'S')))

  /** Billing addresses only (sorted by default) */
  const billingAddresses = computed(() => sortAddressByDefault(addresses.value.filter((address) => address.approachType === 'B')))

  /** User interests (pipe-separated string split into array) */
  const interests = computed(() => (profile.value?.preferences?.interests?.split('|').filter(Boolean) || []))

  /** Default shipping address (main = true) */
  const defaultShippingAddress = computed(() => shippingAddresses.value.find((address) => address.main))

  /** Default billing address (main = true) */
  const defaultBillingAddress = computed(() => billingAddresses.value.find((address) => address.main))

  /** Preferred shoe size (for size recommendations) */
  const preferredShoeSize = computed(() => profile.value?.preferences?.preferredShoeSize)

  /** Active subscriptions (email, SMS) keyed by channel */
  const subscriptions = computed(() => profile.value?.subscriptions
    ? profile.value.subscriptions
        .filter((subscription) => subscription.optin === true)
        .reduce((reducer, subscription) => {
          reducer[subscription.channel] = subscription
          return reducer
        }, {})
    : {}
  )

  /** Has user subscribed to newsletter (email, double opt-in confirmed)? */
  const hasNewsletterSubscription = computed(() => profile.value?.subscriptions?.some(
    (subscription) =>
      subscription.type === 'Newsletter'
      && subscription.channel === 'email'
      && subscription.doubleOptin === true
  ))

  /**
   * Is user partially enrolled? (has email but missing other core data)
   * - true: User registered but incomplete profile
   * - false: User has complete profile or not registered
   */
  const isPartiallyEnrolled = computed(() => {
    if (!details.value)
      return false

    const { birthDate, email, firstName, lastName, postalCode } = details.value

    return (email && !(firstName && lastName && postalCode && birthDate))
  })

  // ────────────────────────────────────────────────────────────────────────────
  // Special Profile Types
  // ────────────────────────────────────────────────────────────────────────────

  /** Is user a sponsored athlete? (annual credit program) */
  const isAthleteProfile = computed(() => !!details.value?.athlete)

  /** Consumer credit type (if not athlete) */
  const creditedConsumerProfileType = computed(
    () => isAthleteProfile.value ? undefined : details.value?.consumerCredit?.type
  )

  /** Is user a Wranx member? (Western industry professional) */
  const isWranxProfile = computed(() => details.value?.identity?.some(({ type }) => type === 'WRANX'))

  /** Is user an IPA member? (Industry Pro Account) */
  const isIpaProfile = computed(() => details.value?.identity?.some(({ type }) => type === 'IPA'))

  /** Is user Wranx OR IPA? */
  const isWranxOrIpaProfile = computed(() => isWranxProfile.value || isIpaProfile.value)

  /** Has user linked social account? (Google, Facebook) */
  const hasSocialIdentity = computed(() => details.value?.identity?.some(({ type }) =>
    isString(type) && ['google', 'facebook'].includes(type.toLowerCase())
  ) ?? false)

  // ────────────────────────────────────────────────────────────────────────────
  // Discount/Credit Messages (Formatted)
  // ────────────────────────────────────────────────────────────────────────────

  /** Employee discount details (formatted message with spend/cap) */
  const employeeDiscountDetails = computed(() => details.value?.employee && replaceAll($t.associateDiscount, {
    currentSpend: $formatPrice(details.value.employee.currentSpend, useCurrencyCode()),
    annualCap: $formatPrice(details.value.employee.annualCap, useCurrencyCode()),
    Brand: $t.brandName
  }))

  /** Athlete credit details (formatted message with spend/cap) */
  const athleteCreditDetails = computed(() => {
    const value = details.value?.athlete
    if (!value) return

    return replaceAll($t.athleteCredit, {
      currentSpend: $formatPrice(value.currentSpend, useCurrencyCode()),
      annualCap: $formatPrice(value.annualCredit, useCurrencyCode()),
      Brand: $t.brandName
    })
  })

  /** IPA discount details (formatted message) */
  const ipaDiscountDetails = computed(() => details.value?.prosumer && replaceAll($t.ipaDiscount, {
    currentSpend: $formatPrice(details.value.prosumer.currentSpend, useCurrencyCode()),
    annualCap: $formatPrice(details.value.prosumer.annualCredit, useCurrencyCode()),
    Brand: $t.brandName
  }))

  /** Wranx discount details (formatted message) */
  const wranxDiscountDetails = computed(() => details.value?.prosumer && replaceAll($t.wranxDiscount, {
    currentSpend: $formatPrice(details.value.prosumer.currentSpend, useCurrencyCode()),
    annualCap: $formatPrice(details.value.prosumer.annualCredit, useCurrencyCode()),
    Brand: $t.brandName
  }))

  /**
   * Banner message for special discount/credit programs.
   * - Priority: Employee > Athlete > IPA > Wranx
   * - Returns undefined if no special profile type
   */
  const spendBannerMessage = computed(() => {
    if (isEmployeeProfile.value && auth.isEmployeeDiscountTCAccepted)
      return employeeDiscountDetails.value
    else if (isAthleteProfile.value)
      return athleteCreditDetails.value
    else if (isIpaProfile.value)
      return ipaDiscountDetails.value
    else if (isWranxProfile.value)
      return wranxDiscountDetails.value
  })

  // ────────────────────────────────────────────────────────────────────────────
  // Enrollment State
  // ────────────────────────────────────────────────────────────────────────────

  /**
   * Determine initial enrollment state based on auth and profile completeness.
   * - LOOKUP: User not logged in
   * - Legacy onboarding: COMPLETE_PROFILE_MOBILE when no phone is stored;
   *   otherwise COMPLETE_PROFILE_PROFILE for incomplete profiles or
   *   COMPLETE_PROFILE_SUCCESS for completed profiles.
   * - Post-auth onboarding: COMPLETE_PROFILE_SUBSCRIPTION when no phone is
   *   stored, otherwise COMPLETE_PROFILE_PROFILE.
   */
  const getInitialEnrollmentState = () => {
    if (!auth.loggedIn)
      return EnrollmentState.LOOKUP

    const hasStoredPhoneNumber = Boolean(details.value?.phone || subscriptions.value.sms?.recipient)

    if ($feature.tempEnablePostAuthOnboarding) {
      return hasStoredPhoneNumber
        ? EnrollmentState.COMPLETE_PROFILE_PROFILE
        : EnrollmentState.COMPLETE_PROFILE_SUBSCRIPTION
    }

    if (!hasStoredPhoneNumber)
      return EnrollmentState.COMPLETE_PROFILE_MOBILE

    if (isPartiallyEnrolled.value)
      return EnrollmentState.COMPLETE_PROFILE_PROFILE

    if (showInterestsStep && interests.value.length < 3)
      return EnrollmentState.COMPLETE_PROFILE_INTERESTS

    return EnrollmentState.COMPLETE_PROFILE_SUCCESS
  }

  /** Current enrollment state (reactive) */
  const enrollmentState = ref(getInitialEnrollmentState())

  // ────────────────────────────────────────────────────────────────────────────
  // Actions (Profile Mutations)
  // ────────────────────────────────────────────────────────────────────────────

  /**
   * Update basic user information.
   * - firstName, lastName, phone, phoneCode, postalCode, gender
   * - Phone formatted to E.164 before API call
   * - Optimistically updates local state on success
   *
   * @param {ConsumerDetails} consumerDetails - Partial profile updates
   * @throws {Error} If profile not loaded
   */
  async function updateBasicInfo(consumerDetails: ConsumerDetails) {
    if (!profile.value) throw new Error('Profile not loaded')
    // check if the consumerProfile is empty, don't run update since it will cause an additional call that will trigger double emails
    if (!Object.values(consumerDetails).some(Boolean)) return

    if (isString(consumerDetails.phone))
      consumerDetails.phone = formatE164phone(consumerDetails.phone, consumerDetails.phoneCode)

    await consumer.$updateProfile({
      consumerProfile: { consumerDetails }
    })
    profile.value.consumerDetails = { ...profile.value.consumerDetails, ...consumerDetails }
  }

  /**
   * Update user interests and shoe size preference.
   * - Interests stored as pipe-separated string
   *
   * @param {object} payload - { interests: string[], preferredShoeSize: string }
   * @throws {Error} If profile not loaded
   */
  async function updateInterests(payload) {
    if (!profile.value) throw new Error('Profile not loaded')
    const preferences = {
      ...profile.value.preferences,
      interests: payload.interests.join('|'),
      preferredShoeSize: payload.preferredShoeSize
    }
    await consumer.$updateProfile({
      consumerProfile: { consumerDetails: {}, preferences }
    })
    profile.value.preferences = preferences
  }

  /**
   * Set an address as default (main = true).
   * - Clears previous default of same type
   * - Updates local state optimistically
   *
   * @param {string} addressId - Address ID (e.g., 'S123456', 'B789012')
   * @throws {Error} If profile not loaded
   */
  function setDefaultAddress(addressId: string) {
    if (!profile.value) throw (new Error('Profile not loaded'))

    const approachType = addressId.substring(0, 1)

    const currentDefaultAddress = profile.value.consumerDetails.address
      .find((address) => address.id !== addressId && address.approachType === approachType && address.main)

    if (currentDefaultAddress)
      currentDefaultAddress.main = false

    profile.value.consumerDetails.address.find((address) => address.id === addressId).main = true
  }

  /**
   * Add new address to user profile.
   * - Phone formatted to E.164
   * - Auto-sets as default if no address of same type exists
   * - Backend generates address ID
   *
   * @param {BaseAddress} payload - Address data
   * @returns {Promise<string>} New address ID
   * @throws {Error} If profile not loaded
   */
  async function addAddress(payload: BaseAddress) {
    if (!profile.value) throw (new Error('Profile not loaded'))

    // ensure the address is flagged as default if the user does not have one already of the same type
    // even if the user does not check the "default" checkbox
    const shouldBeMain = !addresses.value.some(({ approachType }) => approachType === payload.approachType)

    const address = {
      ...payload,
      recipientContactPhone: formatE164phone(payload.phone, payload.phoneCode),
      main: payload.main || shouldBeMain
    } as AddAddressPayload['address']

    const { addressId } = await consumer.$addAddress({ address })

    // First time adding address to My account
    if (!profile.value.consumerDetails.address)
      profile.value.consumerDetails.address = [{ ...address, id: addressId }]
    else
      profile.value.consumerDetails.address.unshift({ ...address, id: addressId })

    if (payload.main)
      setDefaultAddress(addressId)

    return addressId
  }

  /**
   * Save credit card to user profile.
   * 🔒 **PCI-sensitive operation** - requires reCAPTCHA
   * - Card details tokenized by backend
   * - Returns payment instrument ID (not raw card data)
   *
   * @param {object} payload - Card data (tokenized)
   * @param {string} recaptcha_response - reCAPTCHA v3 token
   * @returns {Promise<string | undefined>} Payment instrument ID
   *
   * ⚠️ **NEVER log card numbers, CVV, or full PAN**
   */
  async function saveCreditCard(payload, recaptcha_response) {
    try {
      const { card } = await consumer.$savePaymentInstrument({
        ...payload,
        recaptcha_response
      })

      if (card.paymentInstrumentId) return card.paymentInstrumentId
    }
    catch (err) {
      assertApiError(err)
      const toast = useToaster()
      toast.add({
        autoClose: true,
        props: {
          message: err.errorId === ApiErrorCode.CC_ALREADY_EXISTS
            ? err.message
            : $t.creditCardSaveFailed,
          type: 'error'
        }
      })
    }
  }

  /**
   * Save gift card to user profile.
   * - Card number and PIN tokenized by backend
   *
   * @param {object} payload - Gift card data
   * @returns {Promise<string | undefined>} Payment instrument ID
   */
  async function saveGiftCard(payload) {
    try {
      const { card } = await consumer.$savePaymentInstrument(payload)

      return card.paymentInstrumentId
    }
    catch (err) {
      assertApiError(err)
      const toast = useToaster()
      toast.add({
        autoClose: true,
        props: {
          message: err.errorId === ApiErrorCode.CC_ALREADY_EXISTS
            ? $t.giftCardAlreadyExist
            : $t.giftCardSaveFailed,
          type: 'error'
        }
      })
    }
  }

  /**
   * Update existing address by ID.
   * - Phone formatted to E.164
   * - Can update default flag (main)
   *
   * @param {UpdateAddressPayload['address']} payload - Address updates
   * @returns {Promise<string>} Updated address ID
   * @throws {Error} If profile not loaded
   */
  async function updateAddress(payload: UpdateAddressPayload['address']) {
    if (!profile.value) throw new Error('Profile not loaded')

    const recipientContactPhone = formatE164phone(
      payload.recipientContactPhone || payload.phone,
      payload.phoneCode
    )
    const addressDetails = { ...payload, recipientContactPhone }
    // remove unnecessary data from the body
    delete addressDetails.phone
    delete addressDetails.phoneCode

    const { addressId } = await consumer.$updateAddress(payload.id, {
      address: addressDetails
    })
    const addressIndex = profile.value.consumerDetails.address.findIndex(
      (address) => address.id === addressId
    )
    if (addressIndex !== -1)
      profile.value.consumerDetails.address[addressIndex] = addressDetails

    if (payload.main)
      setDefaultAddress(addressId)

    return addressId
  }

  /**
   * Delete address from user profile.
   *
   * @param {string} id - Address ID to delete
   * @throws {Error} If profile not loaded
   */
  async function deleteAddress(id: string) {
    if (!profile.value) throw (new Error('Profile not loaded'))
    const filteredAddresses = profile.value.consumerDetails.address.filter((address) => address.id !== id)
    await consumer.$deleteAddress(id)
    profile.value.consumerDetails.address = filteredAddresses
  }

  /**
   * Clear user data from store (called on logout).
   */
  const reset = () => {
    data.value = null
  }

  // ────────────────────────────────────────────────────────────────────────────
  // Return (Public API)
  // ────────────────────────────────────────────────────────────────────────────

  return {
    get: execute,
    pending,
    error,
    profile,
    details,
    activeOffers,
    enrollmentState,
    getInitialEnrollmentState,
    shippingAddresses,
    billingAddresses,
    defaultShippingAddress,
    defaultBillingAddress,
    interests,
    preferredShoeSize,
    subscriptions,
    hasNewsletterSubscription,
    spendBannerMessage,
    isAthleteProfile,
    loyaltyPoints,
    isEmployeeProfile,
    creditedConsumerProfileType,
    isPartiallyEnrolled,
    isLoyaltyMember,
    enableLoyaltyFeatures,
    loyaltyOptInDate,
    loyaltyPointsBalance,
    isWranxProfile,
    isIpaProfile,
    isWranxOrIpaProfile,
    hasSocialIdentity,
    reset,
    updateBasicInfo,
    updateInterests,
    addAddress,
    updateAddress,
    deleteAddress,
    saveCreditCard,
    saveGiftCard
  }
})
